import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDeviceAnchor, usePreviewStage } from '../../../components/preview-stage';
import { useConfigurator } from '../../../store/configurator';
import { findThemeScreenshot } from '../../../data/preview';
import { usePreviewSlot } from '../../../lib/staticPreview';
import { useIsTablet } from '../../../lib/deviceType';
import { LAPTOP_FRAME_DEFAULT } from '../../../steps/finish/components/DevicePreview/frame-art';
import { StaticMacDevice, StaticPhoneDevice } from '../../../steps/finish/components/DevicePreview/StaticDevices';
import { PreviewButton, PreviewButtonLabel } from '../../../steps/finish/components/DevicePreview/DevicePreview.styles';
import { DeviceSwitcher } from '../DeviceSwitcher';
import { Pane, DeviceArea, DeviceCenter, DeviceAnchor, SwitchBar } from './PreviewPane.styles';

/** Globe glyph for the "Live Preview" pill; inherits the pill's colour via currentColor
 *  (no colour literal). Same mark as the Finish-step preview button. */
function GlobeIcon(): React.ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/** Laptop + phone frame aspects (width / height; match the bezels). */
const LAPTOP_RATIO = 1113 / 723;
const PHONE_RATIO = 792 / 1619;
/**
 * Transparent band above each bezel's first opaque row, as a fraction of the frame height
 * (measured from the PNG alpha channel). The laptop art has a tall empty band above the
 * lid (69 of 723 rows), so its visible top sits that far below the anchor box's top; the
 * logo clearance is checked against the visible top, not the box.
 */
const LAPTOP_TOP_BAND = 69 / 723;
const PHONE_TOP_BAND = 12 / 1619;
/**
 * Breathing room around the centred device (px); mirrored by the DeviceCenter inset in
 * the styles. There is deliberately NO fixed height cap; both devices grow to fill the
 * pane and use as much of the viewport as possible (both fill the full height below the
 * logo). The side inset matches the sidebar's 16px margin.
 */
const PAD_TOP = 24;
const PAD_SIDE = 16;
const PAD_BOTTOM = 0;
/**
 * Keep-out for the fixed Whitelabels.com lockup (top-left, 230×64px). Both devices are
 * always capped so their centred visible top drops below LOGO_CLEAR: even on a wide pane,
 * where a device sits clear of the logo column, it never rises into the logo's band. The
 * account menu (top-right) shares that band, so it needs no keep-out of its own.
 */
const LOGO_CLEAR = 80;

/**
 * Default main viewport (left side of the wizard) for the build steps: a live device
 * preview of the configured site with a brand logo top-left and a Mobile / Desktop
 * toggle bottom-centre.
 *
 * It renders only the chrome + ONE empty, correctly-sized device ANCHOR; the persistent
 * {@link PreviewStage} host floats the unified morph device over it. Because the host
 * lives above the route Outlet, navigating between the build steps never unmounts or
 * reloads the iframe, and a Mobile/Desktop switch cross-slides the SAME warm frame — so
 * the previewed site's route survives the toggle.
 */
export const PreviewPane = (): React.ReactElement => {
  const { i18n } = useTranslation();
  const { device, setDevice, openFullscreen } = usePreviewStage();

  // No `phoneZoomWidth`: the unified device is not a role device, so the host never reads
  // a zoom width for it. MorphDevice owns both virtual render widths (420 / 1280).
  const deviceRef = useDeviceAnchor({ role: 'mac', mode: 'single', bezel: 'macDefault', unified: true });

  // Tablet path: the persistent iframe host is suppressed on tablets (it crashes iPad
  // Safari), so the anchor is never floated. Instead this pane renders the SAME static
  // screenshot the mobile flow + Finish step use, inside the identical measured box below
  // (so the physical device size matches the iframe exactly). The Mobile/Desktop switcher
  // just picks which capture (desktop or iphone) fills the frame; the live site stays a
  // tap away via the fullscreen "Live Preview" (its own transient iframe).
  const isTablet = useIsTablet();
  const { selectedTheme, selectedColor, selectedStyle } = useConfigurator();
  const lang = (i18n.language || 'en').split('-')[0];
  const screenshot = findThemeScreenshot(selectedTheme, selectedColor, selectedStyle, lang);
  const mac = usePreviewSlot(screenshot?.desktop ?? '');
  const phone = usePreviewSlot(screenshot?.iphone ?? '');

  // Size the anchor to fill the available pane: the device grows to the full height
  // budget (pane minus the padding), bounded by the width so a narrow pane shrinks it
  // instead of overflowing, and clamped so the centred device always clears the fixed
  // logo (see belowLogo). The host reads this box's rect and dresses it with the bezel
  // matching the live `device`.
  const areaRef = React.useRef<HTMLDivElement>(null);
  const [box, setBox] = React.useState({ phoneW: 0, phoneH: 0, macW: 0, macH: 0 });
  React.useEffect(() => {
    const el = areaRef.current;
    if (!el) return undefined;
    const measure = (): void => {
      const areaW = el.clientWidth;
      const areaH = el.clientHeight;
      const w = Math.max(0, areaW - PAD_SIDE * 2);
      const h = Math.max(0, areaH - PAD_TOP - PAD_BOTTOM);
      const centerY = PAD_TOP + (areaH - PAD_TOP - PAD_BOTTOM) / 2;
      // Tallest height for a centred device whose VISIBLE top (box top + the art's
      // transparent band) drops below the fixed logo: centerY - H / 2 + topBand * H >= LOGO_CLEAR.
      // Applied ALWAYS, not only when a device reaches the logo column: on a wide pane a
      // device clears the logo sideways, but filling the height would still lift its top
      // into the logo's band beside it.
      const belowLogo = (topBand: number): number => Math.max(0, (centerY - LOGO_CLEAR) / (0.5 - topBand));
      const phoneH = Math.min(h, w / PHONE_RATIO, belowLogo(PHONE_TOP_BAND));
      const laptopH = Math.min(h, w / LAPTOP_RATIO, belowLogo(LAPTOP_TOP_BAND));
      setBox({
        phoneH,
        phoneW: phoneH * PHONE_RATIO,
        macH: laptopH,
        macW: laptopH * LAPTOP_RATIO,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Pane>
      <DeviceArea ref={areaRef}>
        <DeviceCenter>
          <DeviceAnchor
            ref={deviceRef}
            style={
              device === 'mobile'
                ? { width: box.phoneW || undefined, height: box.phoneH || undefined }
                : { width: box.macW || undefined, height: box.macH || undefined }
            }
          >
            {isTablet ? (
              device === 'mobile' ? (
                <StaticPhoneDevice
                  imageSrc={phone.src}
                  onImageError={phone.onError}
                  topColor={selectedColor?.topNav}
                  bottomColor={selectedColor?.bottomNav}
                  bgColor={selectedColor?.bg}
                  spinner={!phone.exhausted}
                />
              ) : (
                <StaticMacDevice
                  imageSrc={mac.src}
                  onImageError={mac.onError}
                  topColor={selectedColor?.topNav}
                  bgColor={selectedColor?.bg}
                  frame={LAPTOP_FRAME_DEFAULT}
                  spinner={!mac.exhausted}
                />
              )
            ) : null}
          </DeviceAnchor>
        </DeviceCenter>
      </DeviceArea>

      <SwitchBar>
        <DeviceSwitcher value={device} onChange={setDevice} pill={isTablet} />
        {/* On tablet the inline preview is a static image, so surface the live-site
            escape hatch right here (to the RIGHT of the switcher, matched in height via
            the row's default stretch). Desktop keeps its interactive inline preview, so
            it doesn't need this; the "Live Preview" pill stays a Finish-step control. */}
        {isTablet ? (
          <PreviewButton type="button" data-ga-id="cfg-wizard-preview-open" onClick={openFullscreen}>
            <PreviewButtonLabel text="Live Preview" tag="span" />
            <GlobeIcon />
          </PreviewButton>
        ) : null}
      </SwitchBar>
    </Pane>
  );
};

export default PreviewPane;
